import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  CalendarDays,
  CreditCard,
  CupSoda,
  DollarSign,
  Info,
  ShoppingBag,
  UtensilsCrossed,
  LayoutDashboard,
  Landmark,
  Languages,
  LogOut,
  Megaphone,
  Receipt,
  Settings,
  Shield,
  Shirt,
  Loader2,
  Store,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, setShopId } from './lib/api.js';
import { SessionProvider, TelegramLoginButton, useSession } from './lib/session.js';
import {
  Button,
  ErrorBox,
  Empty,
  Field,
  Fieldset,
  Input,
  Panel,
  Select,
  Spinner,
} from './components/ui.js';
import { AnalyticsPage } from './pages/analytics.js';
import { BillingPage } from './pages/billing.js';
import { MarketingPage } from './pages/marketing.js';
import { PolicyPage } from './pages/policy.js';
import { DashboardPage } from './pages/dashboard.js';
import { OrderDetailPage, OrdersPage } from './pages/orders.js';
import { PaymentsPage } from './pages/payments.js';
import { ProductsPage } from './pages/products.js';
import { SettingsPage } from './pages/settings.js';
import { MenuPage } from './pages/menu.js';
import { CalendarPage } from './pages/calendar.js';

/**
 * Lucide rather than emoji.
 *
 * Emoji render from the platform font, so the same nav was a different set of
 * pictures on Android, iOS and Windows — and several of them (👗 🏦 🧾) are
 * drawn at a different weight and colour from everything around them. These
 * are line icons that inherit `currentColor`, so the active tab actually looks
 * active.
 */
/**
 * The shop tabs. Billing is deliberately NOT here: the subscription, the
 * balance and the plan belong to the account (§3), so a tab sitting inside a
 * shop would say they belong to that shop. It lives in the header instead,
 * beside the other account controls.
 */
const NAV = [
  { to: '/', labelKey: 'admin.dashboard', Icon: LayoutDashboard },
  { to: '/orders', labelKey: 'admin.orders', Icon: Receipt },
  { to: '/products', labelKey: 'admin.products', Icon: Shirt },
  /**
   * §4: the two vertical screens. `only` keeps them out of the nav of a shop
   * that has no use for them — a fashion merchant should never see a tab
   * called "Menu", and the routes stay registered either way so a bookmarked
   * URL still resolves rather than 404ing.
   */
  { to: '/menu', labelKey: 'fnb.menu', Icon: UtensilsCrossed, only: 'fnb' },
  { to: '/calendar', labelKey: 'booking.calendar', Icon: CalendarDays, only: 'services' },
  { to: '/payments', labelKey: 'admin.payments', Icon: Landmark },
  { to: '/marketing', labelKey: 'growth.marketing', Icon: Megaphone },
  { to: '/analytics', labelKey: 'growth.analytics', Icon: BarChart3 },
  { to: '/policy', labelKey: 'trust.policy', Icon: Shield },
  { to: '/settings', labelKey: 'admin.settings', Icon: Settings },
] as const;

function Shell() {
  const { status, user, memberships, shopId, selectShop, signOut, locale, setLocale, t, isMiniApp } =
    useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /**
   * The shop's vertical decides the nav. Fetched here rather than threaded
   * through the session because the session is about WHO is signed in, and
   * this is about WHICH shop they are currently looking at.
   */
  const shopQuery = useQuery({
    queryKey: ['shop', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.shop(shopId!),
    staleTime: 5 * 60_000,
  });
  const vertical = shopQuery.data?.shop.vertical ?? 'retail';

  /**
   * The invite route renders before every gate below it.
   *
   * Whoever follows an invite link is, by definition, not a member of anything
   * yet — so the "no shops, make one" screen would meet them instead, and the
   * link would look broken in exactly the way it already did.
   */
  if (pathname.startsWith('/invite')) return <Outlet />;

  if (status === 'loading') return <Spinner label={t('common.loading')} />;
  if (status === 'anonymous') return <SignIn />;
  if (memberships.length === 0 || !shopId) return <CreateShop />;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-5xl flex-col">
      <header
        className="sticky top-0 z-20 flex items-center gap-2 px-4 py-3"
        style={{ background: 'var(--color-panel)', borderBottom: '1px solid var(--color-line)' }}
      >
        <Select
          className="max-w-48 font-semibold"
          value={shopId}
          onChange={(event) => selectShop(event.target.value)}
          aria-label={t('admin.dashboard')}
        >
          {memberships.map((membership) => (
            <option key={membership.shopId} value={membership.shopId}>
              {membership.shopName}
            </option>
          ))}
        </Select>

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => setLocale(locale === 'km' ? 'en' : 'km')}
        >
          {locale === 'km' ? 'EN' : 'ខ្មែរ'}
        </Button>
        <Link to="/billing" title={t('admin.billing')}>
          <Button variant="ghost" size="sm">
            <CreditCard />
            <span className="hidden sm:inline">{t('admin.billing')}</span>
          </Button>
        </Link>
        {/*
          No sign-out inside Telegram. `initData` signs the merchant back in on
          the very next render, so the button could not do what it says — and
          the account it would sign out of is their Telegram account, which we
          have no business appearing to end. On the web it stays, because there
          a stored token is a real thing to discard.
        */}
        {isMiniApp ? null : (
          <Button variant="ghost" size="sm" onClick={signOut} title={user?.firstName}>
            <LogOut />
            <span className="hidden sm:inline">{t('admin.signOut')}</span>
          </Button>
        )}
      </header>

      {/*
        Bottom navigation on a phone, top on a laptop. A merchant using this
        one-handed behind a counter should not have to reach for the top of the
        screen to switch to orders.
      */}
      <nav
        className="order-last flex overflow-x-auto sm:order-none sm:px-2"
        style={{
          background: 'var(--color-panel)',
          borderTop: '1px solid var(--color-line)',
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
        }}
      >
        {NAV.filter((item) => !('only' in item) || item.only === vertical).map((entry) => {
          const active = entry.to === '/' ? pathname === '/' : pathname.startsWith(entry.to);
          return (
            <Link
              key={entry.to}
              to={entry.to}
              className="flex flex-1 flex-col items-center gap-0.5 px-3 py-2 text-[11px] font-medium sm:flex-row sm:gap-2 sm:text-sm"
              style={{
                color: active ? 'var(--color-brand)' : 'var(--color-ink-soft)',
                borderTop: active ? '2px solid var(--color-brand)' : '2px solid transparent',
              }}
            >
              <entry.Icon className="size-5 sm:size-4" aria-hidden="true" />
              <span className="whitespace-nowrap">{t(entry.labelKey as never)}</span>
            </Link>
          );
        })}
      </nav>

      <main className="flex-1 p-4 pb-8">
        <Outlet />
      </main>
    </div>
  );
}

function SignIn() {
  const { signInDev, adoptSession, isMiniApp, signInMiniApp, error, t } = useSession();
  const botUsername = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? '';
  const isDev = import.meta.env.DEV;

  return (
    <div className="mx-auto flex min-h-dvh max-w-sm flex-col items-center justify-center gap-5 p-6">
      <h1 className="text-xl font-bold">TeleStore</h1>
      <p className="text-center text-sm" style={{ color: 'var(--color-ink-soft)' }}>
        {t('admin.signIn')}
      </p>

      {/*
        Inside Telegram the widget is never the answer — `initData` already
        names the user, and the widget's own flow cannot complete in a Telegram
        webview. Reaching this screen there means the `initData` sign-in failed,
        so offer that again rather than a door that leads nowhere.

        On the web, `adoptSession` rather than a reload: `signInWithWidget`
        returns a token, it does not store one, so reloading discarded the
        session and the fresh page rendered this same screen again.
      */}
      {isMiniApp ? (
        <Button onClick={() => void signInMiniApp()}>{t('common.retry')}</Button>
      ) : botUsername ? (
        <TelegramLoginButton botUsername={botUsername} onSession={adoptSession} />
      ) : null}

      {isDev ? (
        <Button variant="outline" onClick={() => void signInDev()}>
          Dev sign-in
        </Button>
      ) : null}

      {error ? <ErrorBox message={error} /> : null}
    </div>
  );
}

/**
 * A radio group drawn as tappable cards.
 *
 * Both questions on the create-shop form are short, closed lists where the
 * options need comparing against each other — and a closed `<select>` shows
 * one option while hiding exactly the ones the merchant is deciding between.
 * On a phone it also opens a native picker over the whole screen for three
 * items.
 */
/**
 * One answer in a `ChoiceGroup`.
 *
 * The icon is not decoration. These options are read on a phone by someone who
 * may be skimming in their second language, and a coloured glyph is recognised
 * before a sentence is read — which is most of what this screen has to do.
 */
interface Choice<T extends string> {
  id: T;
  label: string;
  icon?: ReactNode;
  /** Tile behind the icon. One hue per ANSWER, never per state. */
  tint?: string;
  ink?: string;
}

function ChoiceGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  direction = 'column',
}: {
  name: string;
  value: T;
  options: ReadonlyArray<Choice<T>>;
  onChange(next: T): void;
  /**
   * `row` for two short answers that are read as a pair — USD or KHR is one
   * question, and stacking it makes two lines out of something the eye takes
   * in at once. Stay with `column` when the labels are sentences.
   */
  direction?: 'row' | 'column';
}) {
  return (
    <div className={direction === 'row' ? 'flex gap-2' : 'flex flex-col gap-2'} role="radiogroup">
      {options.map((option) => {
        const active = value === option.id;
        return (
          <label
            key={option.id}
            className={
              'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors' +
              (direction === 'row' ? ' flex-1' : '')
            }
            style={{
              border: `1.5px solid ${active ? 'var(--color-brand)' : 'var(--color-line)'}`,
              background: active ? 'var(--color-brand-soft)' : '#fff',
            }}
          >
            {option.icon ? (
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: option.tint, color: option.ink }}
                aria-hidden="true"
              >
                {option.icon}
              </span>
            ) : null}

            <span className="min-w-0 flex-1 text-sm font-medium">{option.label}</span>

            {/*
              A real radio, styled rather than replaced. Drawing our own circle
              would cost keyboard support, the native focus ring, and the
              grouping a screen reader gets for free from a shared `name`.
            */}
            <input
              type="radio"
              name={name}
              className="size-5 shrink-0 accent-[var(--color-brand)]"
              value={option.id}
              checked={active}
              onChange={() => onChange(option.id)}
            />
          </label>
        );
      })}
    </div>
  );
}

/**
 * What TeleStore integrates with.
 *
 * Four real marks, each fetched from its owner and unaltered — provenance and
 * the conditions that keep showing somebody else's logo legitimate are in
 * `apps/telestore-admin/INTEGRATIONS.md`. The first of those conditions is
 * that the integration is real, which is why this strip is shorter than the
 * set of logos anyone can find: Wing has no `payment_provider`, and the `ai`
 * module is Gemini, not Claude.
 *
 * `object-contain` inside a box bounded on BOTH axes is what lets a square
 * icon and a 4:1 wordmark share a row without either being stretched or
 * cropped — the two things every brand guideline asks you not to do. The
 * marks end up different visual sizes, which is honest: they are different
 * shapes and pretending otherwise is what distortion is.
 */
function Integrations({ t }: { t: ReturnType<typeof useSession>['t'] }) {
  const ITEMS = [
    { key: 'aba', name: 'ABA', src: '/integrations/aba.svg' },
    { key: 'khqr', name: 'KHQR', src: '/integrations/khqr.svg' },
    { key: 'telegram', name: 'Telegram', src: '/integrations/telegram.svg' },
    { key: 'gemini', name: 'Gemini', src: '/integrations/gemini.svg' },
  ] as const;

  return (
    <div className="mt-4">
      <p className="mb-2 text-sm font-semibold">{t('admin.integrationsTitle')}</p>

      <ul className="grid grid-cols-4 gap-2">
        {ITEMS.map((item) => (
          <li key={item.key} className="flex flex-col items-center gap-1">
            <span
              className="flex h-12 w-full items-center justify-center rounded-xl px-2"
              style={{ background: '#ffffff', border: '1px solid var(--color-line)' }}
            >
              <img
                src={item.src}
                alt={item.name}
                className="max-h-6 max-w-full object-contain"
              />
            </span>
            <span
              className="w-full truncate text-center text-[0.65rem]"
              style={{ color: 'var(--color-ink-faint)' }}
            >
              {item.name}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type Vertical = 'retail' | 'fnb' | 'services';
type Currency = 'USD' | 'KHR';

function CreateShop() {
  const { t, refresh, locale, setLocale } = useSession();
  const [name, setName] = useState('');
  /**
   * §6.4: the PRIMARY listing currency, not a display default. Prices are
   * stored in it, line snapshots freeze it at order time, and the KHQR is
   * generated in it. The other currency is always shown beside the total as a
   * platform-supplied reference (§6.8) — which is why there is no "both"
   * option here: a payment QR has to ask for one amount.
   *
   * Unlike the vertical, this one IS editable later in settings.
   */
  const [currency, setCurrency] = useState<Currency>('USD');
  /**
   * §4: a vertical is what a shop IS, and it decides the checkout flow, so it
   * is asked once here rather than buried in settings. It is deliberately not
   * editable afterwards — switching a live shop from retail to F&B would
   * orphan every variant and strand orders mid-flow.
   */
  const [vertical, setVertical] = useState<Vertical>('retail');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createShop({ name: name.trim(), currency, defaultLocale: locale, vertical }),
    onSuccess: () => {
      void refresh().then(() => window.location.reload());
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const VERTICALS: ReadonlyArray<Choice<Vertical>> = [
    {
      id: 'retail',
      label: t('admin.verticalRetail'),
      icon: <ShoppingBag className="size-5" />,
      tint: '#ede9fe',
      ink: '#6d28d9',
    },
    {
      id: 'fnb',
      label: t('admin.verticalFnb'),
      icon: <CupSoda className="size-5" />,
      tint: '#dcfce7',
      ink: '#15803d',
    },
    {
      id: 'services',
      label: t('admin.verticalServices'),
      icon: <CalendarDays className="size-5" />,
      tint: '#ffedd5',
      ink: '#c2410c',
    },
  ];

  const CURRENCIES: ReadonlyArray<Choice<Currency>> = [
    {
      id: 'USD',
      label: 'USD',
      icon: <DollarSign className="size-5" />,
      tint: '#dbeafe',
      ink: '#1d4ed8',
    },
    {
      id: 'KHR',
      label: 'KHR',
      /*
        Larger than the 20px lucide glyph beside it, not equal to it. A Khmer
        numeral sign fills less of its em box than an icon fills its viewBox,
        so matching the numbers makes the riel read as the smaller of the two.
        24px is what makes the pair look the same weight.
      */
      icon: <span className="text-2xl font-bold leading-none">៛</span>,
      tint: '#dbeafe',
      ink: '#1d4ed8',
    },
  ];

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-4 p-5">
      {/*
        The language toggle sits above everything rather than in a card header.
        This is the first thing a merchant ever sees, and until it existed the
        only way to leave Khmer was to finish creating a shop in a language they
        might not read. Spelled out rather than "EN": a button naming the
        language you would switch TO should not do it in the alphabet you are
        trying to get away from.
      */}
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setLocale(locale === 'km' ? 'en' : 'km')}
        >
          <Languages />
          {locale === 'km' ? 'English' : 'ភាសាខ្មែរ'}
        </Button>
      </div>

      <header className="text-center">
        <h1 className="flex flex-wrap items-center justify-center gap-2 text-2xl font-bold">
          {t('admin.createShopHeading')}
          <Store className="size-6" style={{ color: 'var(--color-brand)' }} aria-hidden="true" />
        </h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {t('admin.createShopIntro')}
        </p>
      </header>

      <Panel>
        <Field label={t('admin.shopName')}>
          {/*
            Icon inside the control, not beside the label, so the field reads as
            one object at a glance. `pl-10` clears it and the input keeps its
            own focus ring.
          */}
          <div className="relative">
            <Store
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
              style={{ color: 'var(--color-ink-faint)' }}
              aria-hidden="true"
            />
            <Input
              className="pl-10"
              placeholder={t('admin.shopNamePlaceholder')}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </Field>

        <div className="mt-4">
          <Fieldset legend={t('admin.whatDoYouSell')}>
            <ChoiceGroup
              name="vertical"
              value={vertical}
              options={VERTICALS}
              onChange={(next) => setVertical(next)}
            />
          </Fieldset>
          <p
            className="mt-1.5 flex items-center gap-1.5 text-xs"
            style={{ color: 'var(--color-ink-faint)' }}
          >
            <Info className="size-3.5 shrink-0" aria-hidden="true" />
            {t('admin.verticalFixedHint')}
          </p>
        </div>

        <div className="mt-4">
          <Fieldset legend={t('admin.listingCurrency')} hint={t('admin.listingCurrencyHint')}>
            <ChoiceGroup
              name="currency"
              value={currency}
              options={CURRENCIES}
              onChange={(next) => setCurrency(next)}
              direction="row"
            />
          </Fieldset>
        </div>

        <Integrations t={t} />

        <Button
          className="mt-4 w-full bg-gradient-to-r from-[#2563eb] to-[#7c3aed] text-white hover:opacity-90"
          disabled={create.isPending || name.trim().length < 2}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Loader2 className="animate-spin" /> : <Store />}
          {create.isPending ? t('common.loading') : t('admin.createShop')}
        </Button>

        <p className="mt-2.5 text-center text-xs" style={{ color: 'var(--color-ink-faint)' }}>
          {t('admin.createShopFooter')}
        </p>

        {error ? (
          <div className="mt-3">
            <ErrorBox message={error} />
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/**
 * Accepting a staff invite on the web (§3 `identity-team`).
 *
 * The fallback half of the flow. A shop that has connected its bot shares a
 * `t.me` deep link and the bot handles it; a shop that has not yet — which is
 * most shops during their first hour — shares this URL instead, and until now
 * it landed on the 404 screen.
 *
 * Sign-in comes first because accepting is a write against an account, and
 * there may not be one yet: the whole point of an invite is that it goes to
 * somebody who has never used TeleStore. `SignIn` creates it.
 */
function AcceptInvite() {
  const { token } = useParams({ from: '/invite/$token' });
  const { status, refresh, t } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const accept = useMutation({
    mutationFn: () => api.acceptInvite(token),
    onSuccess: async (result) => {
      /**
       * Select the shop they just joined, not whichever came back first. A
       * staff member who already works at another shop would otherwise land in
       * that one and conclude the link did nothing.
       */
      setShopId(result.shopId);
      await refresh();
      setDone(true);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  /**
   * Fires once, as soon as a session exists. Asking a person who just followed
   * an invite link to press a second button called "accept invite" is a step
   * that exists only because it was easy to build.
   */
  useEffect(() => {
    if (status === 'ready' && !accept.isPending && !accept.isSuccess && !error) accept.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === 'loading') return <Spinner label={t('common.loading')} />;
  if (status === 'anonymous') return <SignIn />;

  if (done) {
    // A full reload rather than a route change: the shell reads memberships at
    // mount, and the new shop has to be in that list before the nav renders.
    window.location.replace('/');
    return <Spinner label={t('common.loading')} />;
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6">
      <Panel title={t('admin.team')}>
        {error ? (
          <>
            <ErrorBox message={error} />
            <p className="mt-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
              {t('admin.inviteInvalid')}
            </p>
          </>
        ) : (
          <Spinner label={t('common.loading')} />
        )}
      </Panel>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  ),
  notFoundComponent: () => <Empty title="404" />,
});

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: DashboardPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/orders', component: OrdersPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/orders/$orderId',
    component: OrderDetailPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/products', component: ProductsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/menu', component: MenuPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/calendar', component: CalendarPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/payments', component: PaymentsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/marketing', component: MarketingPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/analytics', component: AnalyticsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/billing', component: BillingPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/policy', component: PolicyPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: SettingsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: AcceptInvite }),
];

export const router = createRouter({
  routeTree: rootRoute.addChildren(routes),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
